import { Link } from 'gatsby'
import type { Product } from 'src/components/restructure/product/types/product'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import ProductFlag from 'src/components/restructure/product/ProductCard/ProductFlag'
import { formatterPrice } from 'src/utils/formatterPrice'
import { useProductLink } from 'src/sdk/product/useProductLinkV2'

import { BannerProductAddToCart } from '../addToCartButton/BannerProductAddToCart'

type Props = {
  loading: boolean
  error: Error | null
  product: Product | null
  firstAvailableItem: ProductItem | undefined
  locationOnPage?: string
}

export function ProductCard({
  loading,
  error,
  product,
  firstAvailableItem,
  locationOnPage,
}: Props) {
  if (loading) {
    return (
      <div className="bg-restructure-black px-4 py-4">
        <p className="text-body-sm text-white/70">Carregando produto…</p>
      </div>
    )
  }

  if (error || !product || !firstAvailableItem) {
    return (
      <div className="bg-restructure-black px-4 py-4">
        <p className="text-body-sm leading-[1.5] text-white/70">
          Não foi possível carregar o produto no momento.
        </p>
      </div>
    )
  }

  return (
    <ProductCardContent
      product={product}
      firstAvailableItem={firstAvailableItem}
      locationOnPage={locationOnPage}
    />
  )
}

type ProductCardContentProps = {
  product: Product
  firstAvailableItem: ProductItem
  locationOnPage?: string
}

function ProductCardContent({
  product,
  firstAvailableItem,
  locationOnPage,
}: ProductCardContentProps) {
  const { sellerOffer } = firstAvailableItem.sellerDefault
  const productImage = firstAvailableItem.images?.[0]
  const cashPrice = sellerOffer?.cashPrice ?? 0
  const listPrice = sellerOffer?.listPrice ?? 0
  const hasDiscount = listPrice > cashPrice

  const productLink = useProductLink({
    product,
    currentSku: firstAvailableItem,
    index: 0,
    slug: product.linkText,
  })

  return (
    <div className="bg-restructure-black md:max-w-[17.4375rem] sm:max-w-full">
      <Link
        {...productLink}
        className="flex items-start gap-4 md:block md:p-0 p-0 sm:w-full"
      >
        <div className="relative w-[6.8125rem] flex-shrink-0 md:w-full">
          {productImage?.imageUrl && (
            <img
              src={productImage.imageUrl}
              alt={productImage.imageText ?? product.productName}
              className="h-[6.25rem] sm:w-[6.8125rem] md:w-full object-contain md:h-full"
              loading="lazy"
            />
          )}
        </div>

        <div className="flex flex-1 flex-col justify-center md:pl-[4.625rem] md:pb-0 md:pt-4">
          <h3 className="text-[0.9375rem] font-semibold leading-[1.35] text-white">
            {product.productName}
          </h3>

          <div className="mt-1 flex flex-wrap items-center gap-2 md:mt-2">
            {cashPrice > 0 && (
              <p className="text-[0.9375rem] font-bold leading-none text-white">
                {formatterPrice(cashPrice)}
                {sellerOffer?.cashDiscountPercentage ? ' à vista' : null}
              </p>
            )}

            {hasDiscount && (
              <p className="text-body-sm leading-none text-white/70 line-through">
                {formatterPrice(listPrice)}
              </p>
            )}
          </div>
          <div className="mt-0 inline-flex min-h-[22px] items-center bg-restructure-green-1 px-0 py-1 md:mt-1">
            {firstAvailableItem.flags?.length > 0 && (
              <ProductFlag
                flag={firstAvailableItem.flags[0]}
                className="text-[10px] sm:text-xs p-[6px]"
              />
            )}
          </div>
        </div>
      </Link>

      <div className="px-4 pb-4 md:pl-[4.625rem] md:pb-0 md:pt-0">
        <BannerProductAddToCart
          product={product}
          currentSku={firstAvailableItem}
          locationOnPage={locationOnPage}
        />
      </div>
    </div>
  )
}
